import React, { createRef } from "react";
import { NSBox, NSBoxString, NSBoxTextArea, NSButtonGreen, NSColumn, NSLabel, NSRepeater, NSRow } from "namirasoft-site-react";
import { NSARouterMakerResult } from "namirasoft-account-react";
import { BillWatchEntities } from "../BillWatchEntities";
import { BillWatchProps } from "../BillWatchProps";
import { BillWatchLayout } from '../BillWatchLayout';
import { FormulaRow, RuleInputRow, RuleRow } from "namirasoft-bill-watch";
import ConditionLine from "../components/ConditionLine";
import { BillWatchRoutes } from "../BillWatchRoutes";

interface RuleEditPageProps extends NSARouterMakerResult, BillWatchProps
{
	isEdit: boolean;
}

export default class RuleEditPage extends React.Component<RuleEditPageProps>
{
	NSBoxString_Name = createRef<NSBoxString>();
	NSBoxTextArea_Description = createRef<NSBoxTextArea>();
	NSRepeater_Rules = createRef<NSRepeater<ConditionLine, FormulaRow>>();

	override render()
	{
		return (
			<BillWatchLayout<RuleRow, RuleInputRow>
				{...this.props}
				action={{
					description: this.props.isEdit ? "You can update an existing rule by changing its formula, thresholds, change types, or description." : "You can define a new anomaly detection or cost threshold rule to apply when creating or updating watches."
				}}
				pages={{
					entity: BillWatchEntities.rule(this.props),
					edit: {
						...this.props,
						ui: {
							tab_page_edit: {
								editor: {
									getInputRow: () =>
									{
										let name: string = this.NSBoxString_Name.current?.getValue() ?? "";
										let description: string = this.NSBoxTextArea_Description.current?.getValue() ?? "";
										let conditions = this.NSRepeater_Rules.current?.getValue();
										return {
											workspace_id: "",
											name,
											conditions,
											description,
											rule_category: [],
											rule_field: [],
											rule_tag: [],
										};
									},
									setRow: (row) =>
									{
										this.NSBoxString_Name.current?.setValue(row.name);
										this.NSBoxTextArea_Description.current?.setValue(row.description);
										this.NSRepeater_Rules.current?.setValue(row.conditions);
									}
								},
								ui: {
									extra_tabs: [
										{
											title: "Conditions",
											clicked: true,
											getContent: () =>
											{
												return (
													<NSRow style={{ width: "100%", maxWidth: NSBox.width.triple }}>
														<NSLabel
															title="This configuration uses a logical AND among conditions."
															description="Each rule can have multiple conditions, all of which are evaluated independently, and the condition connecting them is a logical AND.
This means that every condition within the rule must be met for the rule to be considered triggered.
If even one condition fails, the rule will not flag an anomaly, and no notification will be generated."
														/>
														<NSRepeater<ConditionLine, FormulaRow>
															ref={this.NSRepeater_Rules}
															title="Conditions"
															required
															createItem={(gIndex: number, lIndex: number, ref: React.RefObject<ConditionLine>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
															{
																return <ConditionLine
																	ref={ref}
																	{...this.props}
																	key={`RoleEditPage_NSRepeater_RulesLine_key_${gIndex}`}
																	index={lIndex}
																	deletable={true}
																	required={true}
																	nullable={true}
																	onChanged={onChange}
																	onDelete={() => onDelete?.(lIndex)}
																/>
															}}
															isEmpty={function (_, value: FormulaRow | null): boolean
															{
																return !ConditionLine.IS_DIRTY(value);
															}}
															getValue={function (item: ConditionLine, checkError: boolean): FormulaRow
															{
																return item.getValue(checkError);
															}}
															setValue={function (item: ConditionLine, _: number, value: FormulaRow | null): void
															{
																if (value)
																	item.setValue(value);
															}}
															style={{ width: "100%", maxWidth: NSBox.width.triple }}
														/>
													</NSRow >
												)
											}
										},
										{
											title: "Simulation",
											clicked: false,
											getContent: () => (
												<NSRow>
													<NSButtonGreen
														title="Batch Simulation"
														onClick={{
															action: () =>
															{
																try
																{
																	window.open(BillWatchRoutes.RuleBatchSimulation, "_blank");
																}
																catch (error)
																{
																	this.props.notifier.onError(error as Error);
																}
															},
															showLoading: false
														}}
													></NSButtonGreen>
													<NSButtonGreen
														title="Simulator"
														onClick={{
															action: () =>
															{
																try
																{
																	window.open(BillWatchRoutes.RuleSimulator, "_blank");
																}
																catch (error)
																{
																	this.props.notifier.onError(error as Error);
																}
															},
															showLoading: false
														}}
													></NSButtonGreen>
												</NSRow>
											)
										}
									]
								}
							},
						}
					}
				}}
			>
				<NSColumn>
					<NSRow>
						<NSBoxString
							ref={this.NSBoxString_Name}
							required
							title="Name"
							style={{ width: "100%" }}
						/>
					</NSRow>
					<NSRow>
						<NSBoxTextArea
							ref={this.NSBoxTextArea_Description}
							required={false}
							title="Description"
							style={{ width: "100%" }}
						/>
					</NSRow>
				</NSColumn>
			</BillWatchLayout>
		);
	}
}